import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { eventBus } from '@/services/eventBus';
import type {
  Relationship,
  RelationshipCategory,
  RelationshipStatus,
  RelationshipIntensity,
  RelationshipTrajectory,
  PowerDynamic,
  EmotionalTone,
  TrustLevel,
  DramaType,
  RelationshipMoment,
  RelationshipImpact
} from '@/types';

export interface CreateRelationshipInput {
  characterAId: string;
  characterBId: string;
  category: RelationshipCategory;
  currentStatus: RelationshipStatus;
  intensity: RelationshipIntensity;
  powerDynamic: PowerDynamic;
  emotionalTone: EmotionalTone;
  trustLevel: TrustLevel;
  dramaTypes: DramaType[];
  subcategory?: string;
  knowledgeAsymmetry?: string;
  howTheyMet?: string;
  sharedHistory?: string;
  conflictSource?: string;
  bond?: string;
  thematicRole?: string;
  trajectory?: RelationshipTrajectory;
  keyMoments?: RelationshipMoment[];
  notes?: string;
}

export interface UpdateRelationshipInput {
  relationshipId: string;
  payload: Partial<Relationship>;
}

type RelationshipMap = Record<string, Relationship[]>;

type RelationshipIndex = Record<string, Set<string>>;

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `relationship-${randomSuffix}`;
};

const normalizeMoment = (moment: RelationshipMoment): RelationshipMoment => ({ ...moment });

export const useRelationshipsStore = defineStore('relationships', () => {
  const relationshipsByManuscript = ref<RelationshipMap>({});
  const indexByCharacter = ref<Record<string, RelationshipIndex>>({});
  const activeManuscriptId = ref<string | null>(null);

  const activeRelationships = computed<Relationship[]>(() => {
    if (!activeManuscriptId.value) {
      return [];
    }

    return relationshipsByManuscript.value[activeManuscriptId.value] ?? [];
  });

  const relationshipCount = computed(() => activeRelationships.value.length);

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;

    if (manuscriptId && !relationshipsByManuscript.value[manuscriptId]) {
      relationshipsByManuscript.value[manuscriptId] = [];
    }

    if (manuscriptId && !indexByCharacter.value[manuscriptId]) {
      indexByCharacter.value[manuscriptId] = {};
    }
  };

  const ensureIndexes = (manuscriptId: string): void => {
    if (!indexByCharacter.value[manuscriptId]) {
      indexByCharacter.value[manuscriptId] = {};
    }
  };

  const registerIndex = (manuscriptId: string, characterId: string, relationshipId: string): void => {
    ensureIndexes(manuscriptId);
    const index = indexByCharacter.value[manuscriptId];

    if (!index[characterId]) {
      index[characterId] = new Set();
    }

    index[characterId].add(relationshipId);
  };

  const unregisterIndex = (manuscriptId: string, characterId: string, relationshipId: string): void => {
    const index = indexByCharacter.value[manuscriptId];
    if (!index || !index[characterId]) {
      return;
    }

    index[characterId].delete(relationshipId);

    if (index[characterId].size === 0) {
      delete index[characterId];
    }
  };

  const emitReferenceUpdates = (manuscriptId: string, characterIds: string[]): void => {
    const unique = Array.from(new Set(characterIds));
    unique.forEach((characterId) => {
      eventBus.emit('entity:referencesUpdated', {
        manuscriptId,
        entityType: 'character',
        entityId: characterId
      });
    });
  };

  const getRelationshipsForManuscript = (manuscriptId: string): Relationship[] => {
    return relationshipsByManuscript.value[manuscriptId] ?? [];
  };

  const persistRelationships = (manuscriptId: string, relationships: Relationship[]): void => {
    relationshipsByManuscript.value = {
      ...relationshipsByManuscript.value,
      [manuscriptId]: relationships
    };

    ensureIndexes(manuscriptId);
    const index: RelationshipIndex = {};

    relationships.forEach((relationship) => {
      if (!index[relationship.characterAId]) {
        index[relationship.characterAId] = new Set();
      }
      index[relationship.characterAId].add(relationship.id);

      if (!index[relationship.characterBId]) {
        index[relationship.characterBId] = new Set();
      }
      index[relationship.characterBId].add(relationship.id);
    });

    indexByCharacter.value = { ...indexByCharacter.value, [manuscriptId]: index };
  };

  const createRelationship = async (input: CreateRelationshipInput): Promise<Relationship> => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot create relationship without an active manuscript.');
    }

    const manuscriptId = activeManuscriptId.value;
    const relationships = [...getRelationshipsForManuscript(manuscriptId)];
    const now = new Date().toISOString();

    const relationship: Relationship = {
      id: generateId(),
      manuscriptId,
      characterAId: input.characterAId,
      characterBId: input.characterBId,
      category: input.category,
      subcategory: input.subcategory ?? '',
      currentStatus: input.currentStatus,
      intensity: input.intensity,
      powerDynamic: input.powerDynamic,
      emotionalTone: input.emotionalTone,
      trustLevel: input.trustLevel,
      trustScore: 0,
      knowledgeAsymmetry: input.knowledgeAsymmetry,
      howTheyMet: input.howTheyMet ?? '',
      sharedHistory: input.sharedHistory ?? '',
      conflictSource: input.conflictSource ?? '',
      bond: input.bond ?? '',
      dramaTypes: input.dramaTypes,
      thematicRole: input.thematicRole,
      trajectory: input.trajectory ?? 'stable',
      keyMoments: (input.keyMoments ?? []).map((moment) => normalizeMoment(moment)),
      createdAt: now,
      updatedAt: now,
      notes: input.notes ?? ''
    };

    relationships.push(relationship);
    persistRelationships(manuscriptId, relationships);

    registerIndex(manuscriptId, relationship.characterAId, relationship.id);
    registerIndex(manuscriptId, relationship.characterBId, relationship.id);

    eventBus.emit('relationship:changed', {
      manuscriptId,
      relationshipId: relationship.id,
      characterIds: [relationship.characterAId, relationship.characterBId],
      action: 'created'
    });
    emitReferenceUpdates(manuscriptId, [relationship.characterAId, relationship.characterBId]);

    return relationship;
  };

  const updateRelationship = async ({ relationshipId, payload }: UpdateRelationshipInput): Promise<Relationship> => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot update relationship without an active manuscript.');
    }

    const manuscriptId = activeManuscriptId.value;
    const relationships = [...getRelationshipsForManuscript(manuscriptId)];
    const index = relationships.findIndex((relationship) => relationship.id === relationshipId);

    if (index === -1) {
      throw new Error('Relationship not found.');
    }

    const existing = relationships[index];
    const updated: Relationship = {
      ...existing,
      ...payload,
      keyMoments: payload.keyMoments ? payload.keyMoments.map((moment) => normalizeMoment(moment)) : existing.keyMoments,
      updatedAt: new Date().toISOString()
    };

    relationships[index] = updated;
    persistRelationships(manuscriptId, relationships);

    if (payload.characterAId && payload.characterAId !== existing.characterAId) {
      unregisterIndex(manuscriptId, existing.characterAId, relationshipId);
      registerIndex(manuscriptId, payload.characterAId, relationshipId);
    }

    if (payload.characterBId && payload.characterBId !== existing.characterBId) {
      unregisterIndex(manuscriptId, existing.characterBId, relationshipId);
      registerIndex(manuscriptId, payload.characterBId, relationshipId);
    }

    const affectedCharacters = new Set<string>([
      existing.characterAId,
      existing.characterBId,
      updated.characterAId,
      updated.characterBId
    ]);

    eventBus.emit('relationship:changed', {
      manuscriptId,
      relationshipId,
      characterIds: Array.from(affectedCharacters),
      action: 'updated'
    });
    emitReferenceUpdates(manuscriptId, Array.from(affectedCharacters));

    return updated;
  };

  const deleteRelationship = async (relationshipId: string): Promise<void> => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot delete relationship without an active manuscript.');
    }

    const manuscriptId = activeManuscriptId.value;
    const relationships = getRelationshipsForManuscript(manuscriptId);
    const relationship = relationships.find((item) => item.id === relationshipId);

    if (!relationship) {
      throw new Error('Relationship not found.');
    }

    const updated = relationships.filter((item) => item.id !== relationshipId);
    persistRelationships(manuscriptId, updated);

    unregisterIndex(manuscriptId, relationship.characterAId, relationshipId);
    unregisterIndex(manuscriptId, relationship.characterBId, relationshipId);

    eventBus.emit('relationship:changed', {
      manuscriptId,
      relationshipId,
      characterIds: [relationship.characterAId, relationship.characterBId],
      action: 'deleted'
    });
    emitReferenceUpdates(manuscriptId, [relationship.characterAId, relationship.characterBId]);
  };

  const replaceRelationships = (manuscriptId: string, relationships: Relationship[]): void => {
    persistRelationships(manuscriptId, relationships.map((relationship) => ({ ...relationship, keyMoments: relationship.keyMoments.map((moment) => normalizeMoment(moment)) })));
  };

  const resetStore = (): void => {
    relationshipsByManuscript.value = {};
    indexByCharacter.value = {};
    activeManuscriptId.value = null;
  };

  const getRelationshipById = (relationshipId: string): Relationship | null => {
    return activeRelationships.value.find((relationship) => relationship.id === relationshipId) ?? null;
  };

  const getRelationshipsByCharacter = (characterId: string): Relationship[] => {
    if (!activeManuscriptId.value) {
      return [];
    }

    const manuscriptId = activeManuscriptId.value;
    const index = indexByCharacter.value[manuscriptId];
    if (!index || !index[characterId]) {
      return [];
    }

    const relationshipIds = Array.from(index[characterId]);
    return relationshipIds
      .map((relationshipId) => getRelationshipById(relationshipId))
      .filter((relationship): relationship is Relationship => Boolean(relationship));
  };

  const removeCharacterReferences = (characterId: string): void => {
    if (!activeManuscriptId.value) {
      return;
    }

    const manuscriptId = activeManuscriptId.value;
    const relationships = getRelationshipsForManuscript(manuscriptId);
    const updatedRelationships: Relationship[] = [];

    relationships.forEach((relationship) => {
      if (relationship.characterAId === characterId || relationship.characterBId === characterId) {
        unregisterIndex(manuscriptId, relationship.characterAId, relationship.id);
        unregisterIndex(manuscriptId, relationship.characterBId, relationship.id);
        return;
      }

      updatedRelationships.push(relationship);
    });

    persistRelationships(manuscriptId, updatedRelationships);
  };

  const recordKeyMoment = (
    relationshipId: string,
    moment: {
      description: string;
      impact: RelationshipImpact;
      chapterId?: string;
      changedStatus?: boolean;
    }
  ): Relationship | null => {
    if (!activeManuscriptId.value) {
      return null;
    }

    const manuscriptId = activeManuscriptId.value;
    const relationships = [...getRelationshipsForManuscript(manuscriptId)];
    const targetIndex = relationships.findIndex((relationship) => relationship.id === relationshipId);

    if (targetIndex === -1) {
      return null;
    }

    const target = relationships[targetIndex];
    const now = new Date().toISOString();
    const entry: RelationshipMoment = {
      id: generateId(),
      description: moment.description,
      impact: moment.impact,
      chapterId: moment.chapterId,
      changedStatus: Boolean(moment.changedStatus),
      createdAt: now
    };

    const updated: Relationship = {
      ...target,
      keyMoments: [...target.keyMoments, entry],
      updatedAt: now
    };

    relationships[targetIndex] = updated;
    persistRelationships(manuscriptId, relationships);

    return updated;
  };

  return {
    activeRelationships,
    relationshipCount,
    setActiveManuscript,
    createRelationship,
    updateRelationship,
    deleteRelationship,
    replaceRelationships,
    resetStore,
    getRelationshipById,
    getRelationshipsByCharacter,
    removeCharacterReferences,
    recordKeyMoment
  };
});
